import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaHeart, FaTimes } from 'react-icons/fa';
import { FiShoppingCart, FiPlus, FiMinus } from 'react-icons/fi';
import DOMPurify from 'dompurify';
import CloudflareImage from '../components/ui/cloudflare-image';
import { getProductBySlug, getProductSizeChart, getSettings } from '../services/publicService';
import { addToCart } from '../services/cartService';
import { getWishlist, addToWishlist, removeFromWishlist } from '../services/wishlistService';
import SizeChart from '../components/SizeChart';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import PageLayout from '../components/PageLayout';
import SEO from '../components/SEO';

// Strict DOMPurify config to prevent XSS (including quill vulnerability CVE-2021-3163)
// DOMPurify automatically strips event handlers (onloadstart, onerror, etc.) by default
const sanitizeHTML = (html) => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'div', 'span'],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'style', 'target', 'rel'],
    ALLOW_DATA_ATTR: false,
    // DOMPurify strips all event handlers by default (including onloadstart from CVE-2021-3163)
    // This config ensures only safe attributes are allowed
    SAFE_FOR_TEMPLATES: true
  });
};

const ProductContainer = styled.div`
  margin-top: 2rem;
  padding: 0 1rem;
  max-width: 100%;
  margin-left: auto;
  margin-right: auto;
  
  @media (min-width: 768px) {
    padding: 0 5%;
  }
  
  @media (min-width: 1024px) {
    padding: 0 150px;
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
  
  @media (min-width: 640px) {
    max-width: 28rem;
    margin-left: auto;
    margin-right: auto;
  }
  
  @media (min-width: 768px) {
    max-width: 100%;
    grid-template-columns: 1fr 1fr;
    gap: 2.5rem;
  }
`;

const GalleryContainer = styled.div`
  position: relative;
`;

const WishlistButton = styled.div`
  position: absolute;
  right: 1.25rem;
  top: 0.75rem;
  z-index: 2;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.25rem;
  width: 100%;
  
  svg {
    cursor: pointer;
    height: 25px;
    width: 25px;
  }
`;

const ImageGallery = styled.div`
  display: flex;
`;

const ThumbnailsWrapper = styled.div`
  width: 80px;
  margin-right: 1rem;
`;

const ThumbnailsContainer = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ThumbnailButton = styled.button`
  padding: 0;
  border: ${props => props.active ? '2px solid #000' : '1px solid #e5e5e5'};
  cursor: pointer;
  
  &:hover {
    border-color: #000;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const MainImageContainer = styled.div`
  flex: 1;
  
  img {
    width: 100%;
    height: auto;
    object-fit: cover;
  }
`;

const ProductDetails = styled.div`
  padding: 0 1rem;
  
  @media (min-width: 768px) {
    padding: 0;
  }
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ProductTitle = styled.div`
  font-size: 1.5rem;
  line-height: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 1.25rem;
  margin-top: 0.75rem;
  
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-top: 0;
  }
`;

const ProductName = styled.h1`
  font-size: 1.5rem;
  font-weight: 500;
  margin: 0;
`;

const Divider = styled.hr`
  height: 1px;
  margin-top: 0.75rem;
  margin-bottom: 0.75rem;
  background-color: #e5e5e5;
  border: 0;
`;

const CoinsInfo = styled.div`
  font-size: 14px;
  
  span {
    font-weight: bold;
  }
`;

const SizeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const SizeHeader = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const SizeLabel = styled.label`
  display: block;
  font-medium;
  color: #111827;
  font-size: 0.875rem;
  opacity: 0.8;
`;

const SizeChartLink = styled.div`
  font-size: 0.75rem;
  color: #48a7ff;
  text-decoration: underline;
  cursor: pointer;
`;

const SizeGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

const SizeButton = styled.button`
  position: relative;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: ${props => props.selected ? '2px solid #000' : '1px solid #e5e5e5'};
  background-color: ${props => props.selected ? '#000' : '#fff'};
  color: ${props => props.selected ? '#fff' : '#000'};
  font-size: 0.875rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? '0.5' : '1'};
  
  &:hover {
    border-color: ${props => props.disabled ? '#e5e5e5' : '#000'};
  }
`;

const UnavailableIcon = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #e53e3e;
  font-size: 1.2rem;
`;

const PriceLabel = styled.label`
  display: block;
  font-medium;
  color: #111827;
  font-size: 0.875rem;
  opacity: 0.8;
`;

const PriceDisplay = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  margin-top: 0.75rem;
  
  div:first-child {
    font-weight: bold;
  }
  
  div:last-child {
    font-size: 0.75rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
`;

const AddToCartButton = styled.button`
  text-transform: uppercase;
  border-radius: 0;
  color: white;
  transition: all;
  border: 2px solid;
  background-color: ${props => props.disabled ? '#ccc' : (props.sizeSelected ? 'black' : '#888')};
  border-color: ${props => props.disabled ? '#ccc' : (props.sizeSelected ? 'black' : '#888')};
  font-medium;
  font-size: 0.75rem;
  padding: 0.5rem 0.75rem;
  height: 2.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.25rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  
  &:hover {
    background-color: ${props => props.disabled ? '#ccc' : (props.sizeSelected ? '#333' : '#777')};
  }
`;

const BuyNowButton = styled.button`
  transition: all;
  transform: scale(1);
  border-radius: 0;
  color: black;
  background-color: transparent;
  border: 1px solid black;
  font-medium;
  font-size: 0.75rem;
  padding: 0.5rem 0.75rem;
  height: 2.75rem;
  width: 10rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  
  &:hover {
    transform: ${props => props.disabled ? 'scale(1)' : 'scale(1.02)'};
  }
  
  div {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const AccordionContainer = styled.div`
  margin-bottom: 0.25rem;
  border-bottom: 1px solid #e5e5e5;
`;

const AccordionButton = styled.button`
  width: 100%;
  padding: 0.5rem 0.5rem 0;
  text-align: left;
  background-color: transparent;
  transition: duration-300;
  border: none;
`;

const AccordionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem;
`;

const AccordionTitle = styled.div`
  font-size: 0.875rem;
`;

const AccordionContent = styled.div`
  padding: 0 2.5rem 2.5rem;
  background-color: white;
  
  p {
    margin-bottom: 1rem;
  }
  
  strong {
    font-weight: bold;
  }
  
  ul {
    list-style-type: disc;
    padding-left: 1.5rem;
  }
  
  li {
    margin-bottom: 0.5rem;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  font-size: 1rem;
  color: #7a7a7a;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem;
  font-size: 1rem;
  color: #e53e3e;
`;

const SizeAvailabilityText = styled.div`
  margin-top: 0.75rem;
  font-size: 0.8rem;
  color: ${props => props.available ? '#2f855a' : '#e53e3e'};
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ProductPage = () => {
  const { id: productSlug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { refreshCart } = useCart();

  const [selectedSize, setSelectedSize] = useState('');
  const [activeAccordion, setActiveAccordion] = useState('');
  const [activeImage, setActiveImage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [product, setProduct] = useState(null);
  const [wishlist, setWishlist] = useState([]);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [showSizeChart, setShowSizeChart] = useState(false);
  const [sizeChartData, setSizeChartData] = useState(null);
  const [coinPercentage, setCoinPercentage] = useState(10);

  // Fetch wishlist
  useEffect(() => {
    if (isAuthenticated()) {
      const fetchWishlist = async () => {
        try {
          const response = await getWishlist();
          if (response.success) {
            setWishlist(response.wishlist.map(item => item._id));
          }
        } catch (err) {
          // console.error('Failed to fetch wishlist:', err);
        }
      };
      fetchWishlist();
    }
  }, [isAuthenticated]);

  // Check if product is in wishlist
  useEffect(() => {
    if (product) {
      setIsWishlisted(wishlist.includes(product._id));
    }
  }, [wishlist, product]);

  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await getProductBySlug(productSlug);
        if (response.success) {
          // console.log('Product data loaded:', response.data);
          setProduct(response.data);

          // If product has a size chart, fetch it
          if (response.data.sizeChart && typeof response.data.sizeChart === 'object') {
            // console.log('Product has size chart:', response.data.sizeChart);
            try {
              const sizeChartResponse = await getProductSizeChart(productSlug);
              if (sizeChartResponse.success) {
                // console.log('Size chart data loaded:', sizeChartResponse.data);
                setSizeChartData(sizeChartResponse.data);
              } else {
                // console.log('Failed to load size chart:', sizeChartResponse.error);
              }
            } catch (err) {
              // console.error('Error fetching size chart:', err);
            }
          }
        } else {
          setError('Product not found');
        }
      } catch (err) {
        // console.error('Error fetching product:', err);
        setError('Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productSlug]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await getSettings();
        if (response.success) {
          setCoinPercentage(response.settings.coinPercentage);
        }
      } catch (err) {
        // console.error('Failed to fetch settings:', err);
      }
    };
    fetchSettings();
  }, []);

  const toggleAccordion = (section) => {
    if (activeAccordion === section) {
      setActiveAccordion('');
    } else {
      setActiveAccordion(section);
    }
  };

  // Calculate coins earned based on coinPercentage setting
  const calculateCoins = (price) => {
    // Apply coinPercentage: if 10%, then 1 coin = £10 spend
    return Math.round(price * (coinPercentage / 100));
  };

  // Toggle size chart modal
  const toggleSizeChart = () => {
    setShowSizeChart(!showSizeChart);
  };

  // Handle add to cart
  const handleAddToCart = async (buyNow = false) => {
    try {
      const isOneSize = !product.sizeChart ||
        (product.sizeVariations?.length === 1 && product.sizeVariations[0]?.size === 'OneSize');

      if (!isOneSize && !selectedSize) {
        toast.error('Please select a size');
        return;
      }

      const sizeToUse = isOneSize ? 'OneSize' : selectedSize;

      // Pass the product data to avoid additional API calls
      const result = await addToCart(product._id, sizeToUse, 1, product);

      if (result.success) {
        // Refresh cart count immediately
        refreshCart();
        toast.success('Product Added to Cart');

        if (buyNow) {
          navigate('/cart');
        }
      }
    } catch (err) {
      toast.error(err.toString());
    }
  };

  // Handle wishlist toggle
  const handleWishlistToggle = async () => {
    if (!isAuthenticated()) {
      toast.info('Please log in to use the wishlist');
      navigate('/login');
      return;
    }

    try {
      if (isWishlisted) {
        await removeFromWishlist(product._id);
        setWishlist(wishlist.filter(id => id !== product._id));
        toast.success('Removed from wishlist');
      } else {
        await addToWishlist(product._id);
        setWishlist([...wishlist, product._id]);
        toast.success('Added to wishlist');
      }
    } catch (err) {
      toast.error(err.toString());
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <LoadingMessage>Loading product details...</LoadingMessage>
      </PageLayout>
    );
  }

  if (error || !product) {
    return (
      <PageLayout>
        <ErrorMessage>{error || 'Product not found'}</ErrorMessage>
      </PageLayout>
    );
  }

  // Get available sizes from product
  const availableSizes = product.sizeVariations
    .filter(size => size.isAvailable && size.stock > 0)
    .map(size => size.size);

  const isOneSizeProduct = !product.sizeChart ||
    (product.sizeVariations.length === 1 && product.sizeVariations[0]?.size === 'OneSize');

  // Get product images
  const images = product.images.length > 0
    ? product.images.map((img, index) => ({
      id: index,
      src: img.url,
      alt: img.alt || product.title
    }))
    : [{ id: 0, src: '/images/placeholder.jpg', alt: product.title }];

  const mainImage = product.images?.find(img => img.isMain)?.url || product.images?.[0]?.url || '/images/placeholder.jpg';
  const productUrl = `${window.location.origin}/product/${product.slug}`;

  return (
    <PageLayout>
      <SEO
        title={product.seoTitle || `${product.title} | TheMadZone`}
        description={product.seoDescription || product.description?.overview?.substring(0, 160) || `Buy ${product.title} at TheMadZone. Premium streetwear fashion.`}
        keywords={product.seoKeywords || `${product.title}, streetwear, fashion, TheMadZone`}
        image={mainImage}
        url={productUrl}
      />
      {/* Size Chart Modal */}
      {sizeChartData && typeof sizeChartData === 'object' && (
        <SizeChart
          isOpen={showSizeChart}
          onClose={toggleSizeChart}
          sizeChartData={sizeChartData}
        />
      )}

      <ProductContainer>
        <ProductGrid>
          {/* Left side - Gallery */}
          <GalleryContainer>
            <WishlistButton onClick={handleWishlistToggle}>
              <FaHeart style={{ fill: isWishlisted ? 'black' : 'none', stroke: 'black', strokeWidth: '20' }} />
            </WishlistButton>
            <ImageGallery>
              <ThumbnailsWrapper>
                <ThumbnailsContainer>
                  {images.map((image, index) => (
                    <ThumbnailButton
                      key={image.id}
                      active={activeImage === index}
                      onClick={() => setActiveImage(index)}
                    >
                      <CloudflareImage src={image.src} alt={`Product view ${index + 1}`} />
                    </ThumbnailButton>
                  ))}
                </ThumbnailsContainer>
              </ThumbnailsWrapper>
              <MainImageContainer>
                <CloudflareImage
                  src={images[activeImage].src}
                  alt={images[activeImage].alt}
                />
              </MainImageContainer>
            </ImageGallery>
          </GalleryContainer>

          {/* Right side - Product Details */}
          <ProductDetails>
            <ProductInfo>
              <ProductTitle>
                <ProductName>{product.title}</ProductName>

              </ProductTitle>

              <Divider />

              <CoinsInfo>
                You will earn <span>{calculateCoins(product.price)} MAD Coins</span> with this purchase
              </CoinsInfo>

              <Divider />

              <SizeSection>
                {!isOneSizeProduct && (
                  <div>
                    <SizeHeader>
                      <SizeLabel>Size</SizeLabel>
                      {product.sizeChart && typeof product.sizeChart === 'object' && sizeChartData && (
                        <SizeChartLink onClick={toggleSizeChart}>SIZE CHART</SizeChartLink>
                      )}
                    </SizeHeader>

                    <SizeGrid>
                      {['XS', 'S', 'M', 'L', 'XL', 'XXL'].map(size => {
                        const isAvailable = availableSizes.includes(size);
                        return (
                          <SizeButton
                            key={size}
                            selected={selectedSize === size}
                            disabled={!isAvailable}
                            onClick={() => isAvailable && setSelectedSize(size)}
                          >
                            {size}
                            {!isAvailable && (
                              <UnavailableIcon>
                                <FaTimes />
                              </UnavailableIcon>
                            )}
                          </SizeButton>
                        );
                      })}
                    </SizeGrid>

                    {selectedSize ? (
                      <SizeAvailabilityText available={true}>
                        Size {selectedSize} is available
                      </SizeAvailabilityText>
                    ) : availableSizes.length === 0 ? (
                      <SizeAvailabilityText available={false}>
                        All sizes are currently out of stock
                      </SizeAvailabilityText>
                    ) : (
                      <SizeAvailabilityText available={true}>
                        Please select a size
                      </SizeAvailabilityText>
                    )}
                  </div>
                )}

                <div>
                  <PriceLabel>Price</PriceLabel>
                  <PriceDisplay>
                    <div>£ {product.price}</div>
                    <div>Inclusive of all taxes</div>
                  </PriceDisplay>
                </div>
              </SizeSection>

              <Divider />

              <ButtonGroup>
                <AddToCartButton onClick={() => handleAddToCart(false)} sizeSelected={isOneSizeProduct || !!selectedSize}>
                  <FiShoppingCart />
                  Add to Cart
                </AddToCartButton>
                <BuyNowButton onClick={() => handleAddToCart(true)}>
                  Buy Now
                </BuyNowButton>
              </ButtonGroup>

              <Divider />

              {/* Accordion Sections */}
              <div>
                <AccordionContainer>
                  <AccordionButton onClick={() => toggleAccordion('details')}>
                    <AccordionHeader>
                      <AccordionTitle>The Details</AccordionTitle>
                      {activeAccordion === 'details' ? <FiMinus size={12} /> : <FiPlus size={12} />}
                    </AccordionHeader>
                  </AccordionButton>

                  {activeAccordion === 'details' && (
                    <AccordionContent>
                      <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(product.description.details || '') }} />
                    </AccordionContent>
                  )}
                </AccordionContainer>

                <AccordionContainer>
                  <AccordionButton onClick={() => toggleAccordion('size')}>
                    <AccordionHeader>
                      <AccordionTitle>Size & Fit</AccordionTitle>
                      {activeAccordion === 'size' ? <FiMinus size={12} /> : <FiPlus size={12} />}
                    </AccordionHeader>
                  </AccordionButton>

                  {activeAccordion === 'size' && (
                    <AccordionContent>
                      <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(product.description.sizeFit || '') }} />
                    </AccordionContent>
                  )}
                </AccordionContainer>

                <AccordionContainer>
                  <AccordionButton onClick={() => toggleAccordion('composition')}>
                    <AccordionHeader>
                      <AccordionTitle>Composition & Care</AccordionTitle>
                      {activeAccordion === 'composition' ? <FiMinus size={12} /> : <FiPlus size={12} />}
                    </AccordionHeader>
                  </AccordionButton>

                  {activeAccordion === 'composition' && (
                    <AccordionContent>
                      <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(product.description.compositionCare || '') }} />
                    </AccordionContent>
                  )}
                </AccordionContainer>

                <AccordionContainer>
                  <AccordionButton onClick={() => toggleAccordion('delivery')}>
                    <AccordionHeader>
                      <AccordionTitle>Delivery & Returns</AccordionTitle>
                      {activeAccordion === 'delivery' ? <FiMinus size={12} /> : <FiPlus size={12} />}
                    </AccordionHeader>
                  </AccordionButton>

                  {activeAccordion === 'delivery' && (
                    <AccordionContent>
                      <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(product.description.deliveryReturns || '') }} />
                    </AccordionContent>
                  )}
                </AccordionContainer>
              </div>
            </ProductInfo>
          </ProductDetails>
        </ProductGrid>
      </ProductContainer>
    </PageLayout>
  );
};

export default ProductPage;
